import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  STAT_STATUS_SELECT_OPTIONS,
  STAT_STATUS_VALUES,
  createStat,
  deleteStat,
  listStats,
  parseDictionaryStats,
  statBelongsToEntry,
  updateStat,
  type DictionaryStat,
  type PatchStatsBody,
  type StatStatus,
} from '../api/statsApi';

type Variant = 'subanta' | 'tinganta';

export type EntryStatsPanelProps = {
  variant: Variant;
  parentId: number;
  rawStats: unknown[] | undefined;
  onReload: () => Promise<void>;
};

type StatsFormValues = {
  status: StatStatus;
  published: boolean;
  ruchi: number;
};

function errMessage(err: unknown): string {
  const ax = err as { response?: { data?: { message?: string } }; message?: string };
  return ax?.response?.data?.message || ax?.message || 'Save failed';
}

function buildPatch(prev: DictionaryStat, next: StatsFormValues): PatchStatsBody {
  const patch: PatchStatsBody = {};
  if (prev.status !== next.status) patch.status = next.status;
  if (prev.published !== next.published) patch.published = next.published;
  if (prev.ruchi !== next.ruchi) patch.ruchi = next.ruchi;
  return patch;
}

export function EntryStatsPanel({ variant, parentId, rawStats, onReload }: EntryStatsPanelProps) {
  const [rows, setRows] = useState<DictionaryStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<DictionaryStat | null>(null);
  const [toggleId, setToggleId] = useState<number | null>(null);
  const [createForm] = Form.useForm<StatsFormValues>();
  const [editForm] = Form.useForm<StatsFormValues>();

  const embedded = useMemo(() => {
    const parsed = parseDictionaryStats(rawStats);
    return parsed.filter((s) => statBelongsToEntry(s, variant, parentId));
  }, [rawStats, variant, parentId]);

  const syncRows = useCallback(async () => {
    if (embedded.length > 0) {
      setRows(embedded);
      return;
    }
    setLoading(true);
    try {
      const all = await listStats();
      setRows(all.filter((s) => statBelongsToEntry(s, variant, parentId)));
    } catch (err: unknown) {
      message.error(errMessage(err) || 'Failed to load stats');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [embedded, variant, parentId]);

  useEffect(() => {
    void syncRows();
  }, [syncRows]);

  const afterMutation = useCallback(async () => {
    await onReload();
    await syncRows();
  }, [onReload, syncRows]);

  const defaultFormValues = (): StatsFormValues => ({
    status: STAT_STATUS_VALUES[0],
    published: false,
    ruchi: 0,
  });

  const openCreate = () => {
    createForm.setFieldsValue(defaultFormValues());
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    try {
      const v = await createForm.validateFields();
      setBusy(true);
      const body =
        variant === 'subanta'
          ? {
              subantaId: parentId,
              status: v.status,
              published: v.published,
              ruchi: v.ruchi,
            }
          : {
              tingantaId: parentId,
              status: v.status,
              published: v.published,
              ruchi: v.ruchi,
            };
      await createStat(body);
      message.success('Stats created');
      setCreateOpen(false);
      createForm.resetFields();
      await afterMutation();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (s: DictionaryStat) => {
    setEditing(s);
    editForm.setFieldsValue({
      status: s.status,
      published: s.published,
      ruchi: s.ruchi,
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editing) return;
    try {
      const v = await editForm.validateFields();
      const patch = buildPatch(editing, v);
      if (Object.keys(patch).length === 0) {
        message.info('No changes');
        setEditOpen(false);
        setEditing(null);
        return;
      }
      setBusy(true);
      await updateStat(editing.id, patch);
      message.success('Stats updated');
      setEditOpen(false);
      setEditing(null);
      await afterMutation();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onTogglePublished = async (s: DictionaryStat, published: boolean) => {
    if (s.published === published) return;
    setToggleId(s.id);
    try {
      await updateStat(s.id, { published });
      message.success('Updated');
      await afterMutation();
    } catch (err: unknown) {
      message.error(errMessage(err));
    } finally {
      setToggleId(null);
    }
  };

  const onDelete = async (id: number) => {
    setBusy(true);
    try {
      await deleteStat(id);
      message.success('Stats deleted');
      await afterMutation();
    } catch (err: unknown) {
      message.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const ruchiRules = [
    { required: true, message: 'Ruchi is required' },
    { type: 'number' as const, min: 0, max: 100, message: 'Ruchi must be 0–100' },
  ];

  const formFields = (
    <>
      <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Status required' }]}>
        <Select options={STAT_STATUS_SELECT_OPTIONS} showSearch optionFilterProp="label" />
      </Form.Item>
      <Form.Item name="published" label="Published" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item name="ruchi" label="Ruchi (0–100)" rules={ruchiRules}>
        <InputNumber min={0} max={100} style={{ width: '100%' }} />
      </Form.Item>
    </>
  );

  const columns: ColumnsType<DictionaryStat> = [
    { title: 'ID', dataIndex: 'id', width: 64 },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 140,
      ellipsis: true,
      render: (v: StatStatus) => STAT_STATUS_SELECT_OPTIONS.find((o) => o.value === v)?.label ?? v,
    },
    {
      title: 'Published',
      dataIndex: 'published',
      width: 100,
      render: (_, s) => (
        <Switch
          checked={s.published}
          loading={toggleId === s.id}
          onChange={(checked) => void onTogglePublished(s, checked)}
        />
      ),
    },
    { title: 'Ruchi', dataIndex: 'ruchi', width: 80 },
    {
      title: 'Actions',
      key: 'act',
      width: 160,
      fixed: 'right',
      render: (_, s) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(s)}>
            Edit
          </Button>
          <Popconfirm title="Delete this stats row?" onConfirm={() => void onDelete(s.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Space style={{ marginBottom: 8 }} align="center" wrap>
        <Typography.Text strong>Stats</Typography.Text>
        <Typography.Text type="secondary">
          <code>/stats</code>
        </Typography.Text>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreate}>
          Add stats
        </Button>
      </Space>
      <Table<DictionaryStat>
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 640 }}
      />

      <Modal
        title="New stats"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          createForm.resetFields();
        }}
        onOk={() => void submitCreate()}
        confirmLoading={busy}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" preserve={false}>
          {formFields}
        </Form>
      </Modal>

      <Modal
        title="Edit stats"
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditing(null);
          editForm.resetFields();
        }}
        onOk={() => void submitEdit()}
        confirmLoading={busy}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" preserve={false}>
          {formFields}
        </Form>
      </Modal>
    </div>
  );
}
